import { measureText, useTheme } from '@chronosphereio/chrono-ui';
import { FontSizeOption } from '@/core';
import { useChartsTheme } from '../context/ChartsProvider';

interface CalculateFontSize {
  text: string;
  fontWeight: number;
  width: number;
  height: number;
  lineHeight: number;
  minSize?: number;
  maxSize?: number;
  fontSizeOverride?: FontSizeOption;
}

/**
 * Find the optimal font size given available space
 */
export function useOptimalFontSize({
  text,
  fontWeight,
  width,
  height,
  lineHeight,
  minSize,
  maxSize,
  fontSizeOverride,
}: CalculateFontSize) {
  const { echartsTheme } = useChartsTheme();
  const { theme } = useTheme();

  // if user has selected a font size in the settings, use it instead of calculating the optimal size
  if (fontSizeOverride !== undefined) {
    return Number(fontSizeOverride);
  }
  const defaultFontFamily = theme.typography.fontFamily;

  const textStyle = echartsTheme.textStyle;
  const fontSize = Number(textStyle?.fontSize ?? 12);
  const fontFamily = textStyle?.fontFamily ?? defaultFontFamily;

  // set the font on the canvas context
  const fontStyle = `${fontWeight} ${fontSize}px ${fontFamily}`;

  // measure the width of the text with the given font style
  const textMetrics = measureText(text, fontStyle);
  if (textMetrics === undefined) {
    // TODO: Should there be fallback behavior for when this API is unavailable?
    throw new Error('Canvas context is null.');
  }

  // check how much bigger we can make the font while staying within the width and height
  const fontSizeBasedOnWidth = (width / textMetrics.width) * fontSize;
  const fontSizeBasedOnHeight = height / lineHeight;

  // return the smaller font size, clamped to optional min/max bounds
  const finalFontSize = Math.min(fontSizeBasedOnHeight, fontSizeBasedOnWidth);
  const cappedFontSize = maxSize ? Math.min(finalFontSize, maxSize) : finalFontSize;
  return minSize ? Math.max(cappedFontSize, minSize) : cappedFontSize;
}
